import { Chat, Info, SentimentSatisfiedAlt } from '@mui/icons-material';
import { BrandAwareness } from 'assets/svg/BrandAwareness';
import { cvtClassName } from 'commons/utils';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';

export const SECTION_TYPE = {
  INFO: 'info',
  MESSAGE: 'message',
  AUDITION_INFO: 'auditionInfo',
  SUBMITTED_INFO: 'submittedInfo',
  NEWS: 'news',
  GROUP_INFO: 'groupInfo',
} as const;
export type SectionType = (typeof SECTION_TYPE)[keyof typeof SECTION_TYPE];

type Props = {
  type: SectionType;
  title?: string;
  height?: number;
  isNoBgColor?: boolean;
  children: React.ReactNode;
  className?: string;
};

export const Section = (props: Props) => {
  const { t: tAuditionInfo } = useTranslationWithLang('auditionInfo');
  const header = () => {
    const headerClassName = cvtClassName([
      'flex',
      'items-center',
      'justify-center',
      'p-1',
      'rounded-t-lg',
      'font-semibold',
    ]);

    const headerStyle = props.isNoBgColor
      ? { color: 'var(--c-textinput-label)', background: 'transparent' }
      : { color: 'white', background: 'var(--c-secondary)' };

    const titleWithIcon = () => {
      const title = props.title ?? '';
      return (
        <>
          {(() => {
            switch (props.type) {
              case SECTION_TYPE.INFO:
                return (
                  <>
                    <div
                      className="w-[30px] h-[30px] rounded-full flex justify-center items-center mr-2"
                      style={{ background: 'var(--c-secondary)' }}
                    >
                      <NotificationsIcon className="text-white" />
                    </div>
                    {title.length > 0 ? title : tAuditionInfo('TU005_001')}
                  </>
                );
              case SECTION_TYPE.MESSAGE:
                return (
                  <>
                    <Chat />
                    {title.length > 0 ? title : tAuditionInfo('TU005_002')}
                  </>
                );
              case SECTION_TYPE.AUDITION_INFO:
                return (
                  <>
                    <Info />
                    {title.length > 0 ? title : tAuditionInfo('TU005_005')}
                  </>
                );
              case SECTION_TYPE.SUBMITTED_INFO:
                return (
                  <>
                    <SentimentSatisfiedAlt />
                    {title.length > 0 ? title : tAuditionInfo('TU005_003')}
                  </>
                );
              case SECTION_TYPE.NEWS:
                return (
                  <>
                    <BrandAwareness
                      color={props.isNoBgColor ? 'var(--c-secondary-color)' : 'white'}
                    />
                    {title.length > 0 ? title : tAuditionInfo('TU005_015')}
                  </>
                );
              case SECTION_TYPE.GROUP_INFO:
                return <>{title.length > 0 ? title : tAuditionInfo('TU005_045')}</>;
            }
          })()}
        </>
      );
    };

    return (
      <>
        <h2 className={headerClassName} style={headerStyle}>
          {titleWithIcon()}
        </h2>
      </>
    );
  };

  const contentClassName = cvtClassName([
    'h-full',
    'min-h-[120px]',
    'max-h-full',
    'text-[--c-text]',
    'rounded-b-lg ',
    'bg-[--c-background2] ',
  ]);
  // TODO: "max-h-[XXXpx]"のXXXが320以上になるとクラスが無効になるため、以下で対応
  const contentStyle = props.height ? { maxHeight: `${props.height}px` } : {};

  return (
    <section className={'w-full ' + 'h-full ' + (props.className || '')}>
      {header()}
      <div className={contentClassName} style={contentStyle}>
        {props.children}
      </div>
    </section>
  );
};
